import React, { useState, Fragment, useMemo } from 'react';
import Modal from '../../../Components/Modal';
import { useCalendarContext } from '../Provider';
import DateTime from '../../../Components/Formatting/DateTime';
import UserName from '../../../Components/Formatting/UserName';
import { plural } from '../../../Services/helpers';
import { useScheduleWeek } from '../../../Data/Jobs';
import { useSocketListener, useSocketRecipient } from '../../../Components/Socket';
import Loading from '../../../Components/Loading';
import Icon from '../../../Components/Icons/Icon';
import NoValue from '../../../Components/EmptyStates/NoValue';
import Form, { Actions, EventTriggers } from '../../../Components/Form';
import { eventTriggers } from '../../../Data/Misc';

const ScheduleWeek = ({isOpen, setIsOpen, then, limitToDate}) => {

    return <Modal title={"Schedule jobs"} isOpen={isOpen} setIsOpen={setIsOpen} dismissible>
        {isOpen && <Inner {...{then, limitToDate}} />}
    </Modal>
}

const Inner = ({then, limitToDate}) => {
    const {calendar, selectedWeek, aggregateData, clearMoves} = useCalendarContext();
    const scheduleWeek = useScheduleWeek();
    const socketTopic = `${calendar.id}:${selectedWeek}`;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const aggregatedData = useMemo(() => aggregateData(limitToDate), []);
    const [working, setWorking] = useState(null);

    useSocketListener(
        {topic: socketTopic},
        (ev) => setWorking(working => [...working.filter(w => w !== ev.date)]),
    );
    const socketRecipient = useSocketRecipient(socketTopic);

    if (!aggregatedData) {
        return null;
    }

    const scheduleJobs = async (data) => {
        setWorking(Object.keys(aggregatedData));
        await scheduleWeek({
            socketRecipient,
            dates: aggregatedData,
            eventTriggers: data.eventTriggers,
        });
        setWorking([]);
        await then();
        clearMoves();
    }

    return <Form onSubmit={scheduleJobs} afterSubmit={then} closeModalOnSubmit>
        <div className="flex flex-col divide-y text-sm">
            {Object.keys(aggregatedData).map((date) => {
                const grants = aggregatedData[date];

                return <div key={date} className="flex flex-col divide-y">
                    <DayRow {...{date, grants, working}} />
                </div>
            })}
        </div>

        <EventTriggers triggers={[eventTriggers.SEND_NOTIFICATIONS]} />
        <Actions submit={{label: 'Schedule'}} cancel />
    </Form>
}

const DayRow = ({date, grants, working}) => {
    const total = grants.reduce((acc, grant) => acc + grant.jobs.length, 0);

    if (total === 0) {
        return <div className="py-2 grid grid-cols-2 gap-1 ml-4 text-muted">
            <DateTime date={date} className="font-bold" hideTime />
            <div>
                No jobs
            </div>
        </div>;
    }

    return <div className="py-2 grid grid-cols-2 gap-1 ml-4">
        <div>
            <DateTime date={date} className="font-bold" hideTime />
        </div>
        <div className="relative">
            {working && <div className="float-right text-right">
                    {(working[0] === date) && <Loading inline />}
                    {!working.includes(date) && <Icon icon="Check" className="fill-brand" />}
                </div>}
        </div>

        {grants.map((grant, i) => <Fragment key={i}>
            <div className="pl-4">
                <UserName userId={grant.assignTo} empty="Unassigned" />
            </div>
            <div className="relative">
                {plural(grant.jobs.length, <NoValue />, '1 job', `${grant.jobs.length} jobs`)}
            </div>
        </Fragment>)}
    </div>
}

export default ScheduleWeek;
